import { SessionStorage } from '../storage/session/SessionStorage';
import { isProdEnv } from '../utils/env';
import URLControllerInstance from '../utils/url';

import { LoggerConfiguration, LogTargets } from './config';
import { sessionBitMask } from './SessionBitMask';
import {
  BatchCategories,
  type BatchPayload,
  type IDurationPayload,
  type IEventPayload,
  type IFailurePayload,
  type ILogPayloadBase,
  type IUniqueEventPayload,
  LoggerEndpoint,
  LoggingType,
  LogLevel,
  LogTarget,
  VideoStatsEvent,
} from './types';

const LOG_ENDPOINTS = ['https://stats-collector.zprnnrkgtbfiiigm.net'];

class LoggerRoot {
  private readonly config: Record<string, LoggingType> = LoggerConfiguration;
  private readonly logTargets: Record<string, LogTarget> = LogTargets;
  private environment: string;
  private application: string;
  private location: string;
  private casino: string | null;
  private variant: string | null;
  private bandwidth: number = 0;
  private gameState: string = '';
  private gameVersion?: string;
  private coreVersion?: string;
  private buildNumber?: string;
  private windowWidth: number = window.innerWidth;
  private windowHeight: number = window.innerHeight;

  private cache: {
    [LoggerEndpoint.Event]: Array<IEventPayload>;
    [LoggerEndpoint.UniqueEvent]: Array<IUniqueEventPayload>;
    [LoggerEndpoint.Duration]: Array<IDurationPayload>;
    [LoggerEndpoint.Failure]: Array<IFailurePayload>;
  };

  constructor() {
    this.environment = location.host;
    this.application = location.pathname;
    this.location = '';
    this.casino = '';

    this.cache = {
      [LoggerEndpoint.Event]: [],
      [LoggerEndpoint.UniqueEvent]: [],
      [LoggerEndpoint.Duration]: [],
      [LoggerEndpoint.Failure]: [],
    };

    // retrieve game version from root element
    const rootDataSet = document.getElementById('root')?.dataset;
    if (rootDataSet) {
      this.gameVersion = rootDataSet.gameVersion;
      this.coreVersion = rootDataSet.coreVersion;
      this.buildNumber = rootDataSet.buildNumber;
    }
    // log application version to console
    const completeVersion = this.getCompleteVersion();
    if (completeVersion) {
      console.log(`Application version: ${completeVersion}`);
    }

    const urlSearchParams = new URLSearchParams(location.search);
    const casinoId = URLControllerInstance.getParamValue('casino_id', urlSearchParams);
    this.variant = URLControllerInstance.getParamValue('operator_theme', urlSearchParams);
    if (casinoId) {
      this.casino = casinoId;
    }
    for (const [key, value] of Object.entries(this.config)) {
      const overrideKeyValue = urlSearchParams.get(key);
      if (overrideKeyValue) {
        this.config[key].type = 'console';
      }
    }

    window.addEventListener('beforeunload', () => {
      SessionStorage.setJson('previousSession', {
        sessionBitMask: sessionBitMask.toString(2),
      });
      Logger.logDuration(
        'performance',
        'session',
        Date.now(),
        Math.round(performance.now()),
        this.gameState,
        false,
        sessionBitMask
      );
      this.processLogs();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        SessionStorage.setJson('previousSession', {
          sessionBitMask: sessionBitMask.toString(2),
        });
        this.processLogs();
      }
    });

    window.addEventListener('error', (event) => {
      this.logFailure('errors', event.error?.name, 'general', Date.now(), 1, event.message, false);
      this.logException(event.error);
    });

    window.addEventListener(
      'resize',
      () => {
        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;
      },
      { passive: true }
    );

    this.logEvent('sessionStorage', {
      additional: SessionStorage.isAvailable() ? 'available' : 'unavailable',
    });

    setInterval(() => {
      this.processLogs();
    }, 60_000);
  }

  public getApplication(): string {
    return `${this.application}${this.variant ? `-${this.variant}` : ''}`;
  }

  public getCompleteVersion(): string | undefined {
    if (!this.coreVersion || !this.gameVersion || !this.buildNumber) {
      return;
    }
    return `${this.gameVersion} (${this.coreVersion}) ${this.buildNumber}`;
  }

  private getBasePayload(): ILogPayloadBase {
    return {
      timestamp: Date.now(),
      additional: '',
      environment: this.environment,
      application: this.getApplication(),
      location: this.location,
      casino: this.casino,
      os: '',
      browser: '',
      version: '',
      width: this.windowWidth,
      height: this.windowHeight,
      bandwidth: this.bandwidth,
    };
  }

  private static instance: LoggerRoot;

  public static getInstance(): LoggerRoot {
    if (!this.instance) {
      this.instance = new LoggerRoot();
    }
    return this.instance;
  }

  public updateBandwidth(value: number): void {
    this.bandwidth = value;
  }

  public updateLocation(value: string): void {
    this.location = value;
  }

  public updateGameState(value: string): void {
    this.gameState = value;
  }

  public logInfo(logger_name: string, log_level: LogLevel, info: string): void {
    const timestamp = Date.now();
    const loggingType = this.config?.[logger_name];

    if (loggingType?.type === 'console') {
      if (loggingType.level === 'info' || loggingType.level === 'debug') {
        console.info({ timestamp, info });
      } else if (loggingType.level === 'warning') {
        console.warn({ timestamp, info });
      } else if (loggingType.level === 'error') {
        console.error({ timestamp, info });
      }
    }
  }

  private processLogs(): void {
    if (!(Object.keys(this.cache) as LoggerEndpoint[]).some((key) => this.cache[key].length !== 0)) return;

    this.performLog(
      {
        [BatchCategories.Events]: this.cache[LoggerEndpoint.Event],
        [BatchCategories.UniqueEvents]: this.cache[LoggerEndpoint.UniqueEvent],
        [BatchCategories.Durations]: this.cache[LoggerEndpoint.Duration],
        [BatchCategories.Failures]: this.cache[LoggerEndpoint.Failure],
      },
      'batch',
      'server'
    );
    this.cache = {
      [LoggerEndpoint.Event]: [],
      [LoggerEndpoint.UniqueEvent]: [],
      [LoggerEndpoint.Duration]: [],
      [LoggerEndpoint.Failure]: [],
    };
  }

  public logDuration(
    logger_name: string,
    metric: string,
    timestamp: number,
    duration: number,
    key: string,
    instant: boolean,
    bandwidth?: number
  ): void {
    const data = {
      ...this.getBasePayload(),
      timestamp: Date.now(),
      duration: duration,
      metric: metric,
      additional: key,
      count: 1,
      bandwidth: bandwidth || this.bandwidth,
    };

    this.dispatchLog(data, instant, logger_name, LoggerEndpoint.Duration);
  }

  public logEvent(
    metric: string,
    options?: {
      loggerName?: string;
      count?: number;
      additional?: string;
      instant?: boolean;
      bandwidth?: number;
    }
  ): void;
  public logEvent(
    logger_name: string,
    metric: string,
    timestamp: number,
    count: number,
    key: string,
    instant: boolean,
    bandwidth?: number
  ): void;
  public logEvent(
    metricOrLoggerName: string,
    optionsOrMetric?:
      | string
      | {
          loggerName?: string;
          count?: number;
          additional?: string;
          instant?: boolean;
          bandwidth?: number;
        },
    timestamp?: number,
    count?: number,
    key?: string,
    instant?: boolean,
    bandwidth?: number
  ): void {
    let data: IEventPayload;
    let isInstant: boolean;
    let loggerName: string;
    if (typeof optionsOrMetric !== 'string') {
      const options = optionsOrMetric;
      loggerName = options?.loggerName ?? 'click';
      isInstant = options?.instant ?? false;
      data = {
        ...this.getBasePayload(),
        count: options?.count || 1,
        metric: metricOrLoggerName,
        additional: options?.additional || '',
        bandwidth: options?.bandwidth ? options.bandwidth : this.bandwidth,
      };
    } else {
      loggerName = metricOrLoggerName;
      isInstant = instant ?? false;
      data = {
        ...this.getBasePayload(),
        timestamp: timestamp ? timestamp : Date.now(),
        count: count ? count : 1,
        metric: optionsOrMetric,
        additional: key ? key : '',
        bandwidth: bandwidth ? bandwidth : this.bandwidth,
      };
    }
    this.dispatchLog(data, isInstant, loggerName, LoggerEndpoint.Event);
  }
  public logUniqueEvent(
    userId: string,
    metric: string,
    options?: {
      loggerName?: string;
      count?: number;
      payload?: string;
      numberPayload?: number;
      instant?: boolean;
    }
  ): void {
    const loggerName = options?.loggerName ?? 'click';
    const isInstant = options?.instant ?? false;
    const data: IUniqueEventPayload = {
      timestamp: Date.now(),
      userId: userId,
      metric: metric,
      payload: options?.payload || '',
      numberPayload: options?.numberPayload || 0,
      casino: this.casino,
      location: this.location,
      application: this.getApplication(),
      appVersion: this.getCompleteVersion(),
      environment: this.environment,
      count: options?.count || 1,
    };

    this.dispatchLog(data, isInstant, loggerName, LoggerEndpoint.UniqueEvent);
  }

  public logFailure(
    logger_name: string,
    type: string,
    place: string,
    timestamp: number,
    count: number,
    key: string,
    instant: boolean
  ): void {
    const data = {
      ...this.getBasePayload(),
      timestamp: Date.now(),
      count: count,
      type: type,
      place: place,
      additional: key,
    };

    this.dispatchLog(data, instant, logger_name, LoggerEndpoint.Failure);
  }

  private dispatchLog(
    payload: IEventPayload,
    instant: boolean,
    loggerName: string,
    endpoint: LoggerEndpoint.Event
  ): void;
  private dispatchLog(
    payload: IUniqueEventPayload,
    instant: boolean,
    loggerName: string,
    endpoint: LoggerEndpoint.UniqueEvent
  ): void;
  private dispatchLog(
    payload: IDurationPayload,
    instant: boolean,
    loggerName: string,
    endpoint: LoggerEndpoint.Duration
  ): void;
  private dispatchLog(
    payload: IFailurePayload,
    instant: boolean,
    loggerName: string,
    endpoint: LoggerEndpoint.Failure
  ): void;
  private dispatchLog(
    payload: IEventPayload | IUniqueEventPayload | IDurationPayload | IFailurePayload,
    instant: boolean,
    loggerName: string,
    endpoint: LoggerEndpoint
  ): void {
    if (instant || this.logTargets[loggerName] !== 'server') {
      this.performLog(payload, endpoint, this.logTargets[loggerName]);
    } else {
      // TODO: never type is used to avoid type checking, but it should be fixed
      this.cache[endpoint].push(payload as never);
    }
  }

  private performLog(
    data: IEventPayload | IUniqueEventPayload | IDurationPayload | IFailurePayload | BatchPayload,
    endpoint: LoggerEndpoint | 'batch',
    target: string
  ): void {
    switch (target) {
      case 'server':
        try {
          LOG_ENDPOINTS.forEach((baseUrl) => {
            navigator.sendBeacon(`${baseUrl}/log/${endpoint}`, JSON.stringify(data));
          });
        } catch (e) {
          //
        }
        break;
      case 'console':
        console.log(data);
        break;
    }
  }

  /* Video player */
  public logVideoPlayerStats(url: string, event: string, stats: object): void {
    const method = event === VideoStatsEvent.StorePlayerDetails ? 'PUT' : 'POST';
    if (method == 'PUT') {
      const req = new XMLHttpRequest();
      req.open(method, url, true);
      req.setRequestHeader('Content-Type', 'application/json');
      req.send(JSON.stringify(stats));
    } else {
      try {
        const blob = new Blob([JSON.stringify(stats)], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } catch (e) {
        // Chrome < 81 | does not support sending Blob with sendBeacon
        if (e instanceof DOMException) {
          const req = new XMLHttpRequest();
          req.open(method, url, true);
          req.setRequestHeader('Content-Type', 'application/json');
          req.send(JSON.stringify(stats));
        }
      }
    }
  }

  /* Exceptions player */
  public logException(error: Error): void {
    this.sendExceptionBeacon(error);
  }

  private sendExceptionBeacon(error: Error): void {
    try {
      const payloadList: string[] = [];
      payloadList.push(`${this.application} | ${this.environment} | ${this.getCompleteVersion()}`);
      payloadList.push(error.message);
      error.stack && payloadList.push(error.stack);
      const payload = payloadList.join('\n');
      if (isProdEnv()) {
        LOG_ENDPOINTS.forEach((baseUrl) => {
          navigator.sendBeacon(`${baseUrl}/exception`, payload);
        });
      } else {
        console.error(
          `There was an attempt to send exception beacon. Beacon is sent only on production environment. This would have been its payload:\n${payload}`
        );
      }
    } catch (e) {
      //
    }
  }

  // TODO: Move Logger Name to getInstance function to be able to use multiple pre-configured Loggers at same time
  public logError(
    logger_name: string,
    log_level: LogLevel,
    timestamp: number,
    key: string,
    stacktrace?: unknown
  ): void {
    const loggingType = this.config[logger_name];
    if (loggingType?.type === 'console') {
      if (loggingType.level === 'info' || loggingType.level === 'debug') {
        console.info({ timestamp, key, stacktrace });
      } else if (loggingType.level === 'warning') {
        console.warn({ timestamp, key, stacktrace });
      } else if (loggingType.level === 'error') {
        console.error({ timestamp, key, stacktrace });
      }
    } else if (loggingType?.type === 'server') {
      //todo: make server logging
    }
  }
}

export const Logger = LoggerRoot.getInstance();

interface MethodsCalledMap extends Record<string, boolean> {}

class LogOnce {
  private static extendedLoggerInstance: LogOnce;
  methodsCalled: MethodsCalledMap = {};

  public static getInstance(): LogOnce {
    if (!this.extendedLoggerInstance) {
      this.extendedLoggerInstance = new LogOnce();
    }
    return this.extendedLoggerInstance;
  }

  public logFailureOnce(
    logger_name: string,
    type: string,
    place: string,
    timestamp: number,
    count: number,
    key: string,
    instant: boolean
  ): void {
    if (!this.methodsCalled[`${this.logFailureOnce.name}_${type}`]) {
      Logger.logFailure(logger_name, type, place, timestamp, count, key, instant);
      this.methodsCalled[`${this.logFailureOnce.name}_${type}`] = true;
    }
  }
}
export const LoggerLogOnce = LogOnce.getInstance();
